import { hashKey, type QueryClient, type QueryKey } from "@tanstack/svelte-query";
import type { LegacyGetSearchDataDtoWithIntId } from "../api/types";
import db from "../database/database";
import { errorLog } from "../utils/logger";
import { keys } from "./keys";

// Server data is saved in the encrypted db() under the keys the React app's atoms used, in the same
// shapes, so the two builds share one cache and a rollback to the React app keeps it.

const STORAGE_KEYS: ReadonlyMap<string, string> = new Map([
  [hashKey(keys.searches), "apiIzSearches"],
  [hashKey(keys.shl), "apiSHLinks"],
  [hashKey(keys.providerConfigs), "providerConfigs"],
  [hashKey(keys.devices), "devices"],
]);

/** Where a query's data is saved, if it is. */
export function storageKey(queryKey: QueryKey): string | undefined {
  const [iz, search, searchUid, part] = queryKey;
  if (
    queryKey.length === 4 &&
    iz === "iz" &&
    search === "search" &&
    typeof searchUid === "string"
  ) {
    if (part === "record") {
      return `apiRecords_${searchUid}`;
    }
    if (part === "screenings") {
      return `apiScreenings_${searchUid}`;
    }
  }
  return STORAGE_KEYS.get(hashKey(queryKey));
}

/**
 * False for the placeholder the React app's atoms held until their first fetch,
 * `{ isHydrated: false }`, in case one was saved.
 */
export function isHydrated(value: unknown): boolean {
  return !(
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "isHydrated" in value &&
    value.isHydrated === false
  );
}

async function load(queryKey: QueryKey): Promise<unknown> {
  const key = storageKey(queryKey);
  if (key === undefined) {
    return undefined;
  }
  try {
    const value = await db().getItem<unknown>(key);
    // The React app read a stored null as "never fetched" too.
    return value !== null && isHydrated(value) ? value : undefined;
  } catch (err) {
    errorLog(err, `Could not read ${key}`);
    return undefined;
  }
}

/**
 * Fill the cache with the data the last session saved, without replacing anything already there.
 * It shows at once and counts as out of date, so each query refetches when it's first used.
 */
async function hydrate(client: QueryClient): Promise<void> {
  const loaded = await load(keys.searches);
  const searches = Array.isArray(loaded)
    ? (loaded as LegacyGetSearchDataDtoWithIntId[])
    : undefined;
  const searchUids = (searches ?? []).map(({ attributes }) => attributes.uid);
  const others: QueryKey[] = [
    keys.shl,
    keys.providerConfigs,
    keys.devices,
    ...searchUids.flatMap((uid) => [keys.record(uid), keys.screenings(uid)]),
  ];
  const saved: [QueryKey, unknown][] = [
    [keys.searches, searches],
    ...(await Promise.all(
      others.map(async (queryKey): Promise<[QueryKey, unknown]> => [
        queryKey,
        await load(queryKey),
      ]),
    )),
  ];

  const restored = new Set<string>();
  for (const [queryKey, data] of saved) {
    if (data !== undefined && client.getQueryData(queryKey) === undefined) {
      client.setQueryData(queryKey, data, { updatedAt: 1 });
      restored.add(hashKey(queryKey));
    }
  }
  void client.invalidateQueries({ predicate: (query) => restored.has(query.queryHash) });
}

export interface Persister {
  /**
   * Stop saving, as the session ends: writes not started yet are dropped. Resolves once the write
   * in progress, if any, is done, so db() can be cleared without it landing afterwards.
   */
  stop(): Promise<void>;
}

/**
 * Restore the last session's server data into `client`, then save fresh answers as they arrive.
 * Call once db() is unlocked, and `stop()` before it's cleared.
 *
 * Writes go one at a time. A key waiting to be written keeps only its newest data, so a burst of
 * polling answers costs one write, not one each.
 */
export async function persistQueries(client: QueryClient): Promise<Persister> {
  await hydrate(client);

  const waiting = new Map<string, unknown>();
  let writing: Promise<void> | undefined;
  const writeAll = async () => {
    // Keys set while a write is in progress join the end of this loop.
    for (const [key, data] of waiting) {
      waiting.delete(key);
      try {
        await db().setItem(key, data);
      } catch (err) {
        errorLog(err, `Could not save ${key}`);
      }
    }
    writing = undefined;
  };

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success") {
      return;
    }
    const key = storageKey(event.query.queryKey as QueryKey);
    if (key !== undefined) {
      waiting.set(key, event.query.state.data);
      writing ??= writeAll();
    }
  });

  return {
    async stop() {
      unsubscribe();
      waiting.clear();
      await writing;
    },
  };
}
